import { useEffect, useRef } from 'react';
import { useGLTF, useAnimations, Stage } from '@react-three/drei';
import { AnimationAction } from 'three'
import { pushFaroEvent } from '../../../faroSession';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { AvatarSceneProps } from '../AvatarScene.types';

export const Avatar = ({ mode = 'IDLE' }: AvatarSceneProps) => {
    const { scene, animations } = useGLTF('assets/models/Sam69.glb');
    const { actions } = useAnimations(animations, scene);
    const { gl } = useThree();

    const intervalRef = useRef<ReturnType<typeof setTimeout>>();
    const lastAnimPlayedRef = useRef<AnimationAction | null>(null);

    // Global renderer settings
    useEffect(() => {
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 0.46;
    }, [gl]);

    // Setup lights
    useEffect(() => {
        const ambient = new THREE.AmbientLight(0xffffff, 3.5);
        const spotlight = new THREE.SpotLight(0xffffff, 1.5);
        spotlight.position.set(-0.411, 0.178, 0.421); // Adjust position as needed
        spotlight.angle = Math.PI / 4.5;
        spotlight.penumbra = 1;
        spotlight.decay = 1;
        spotlight.castShadow = true;
        // Increase shadow camera range
        spotlight.shadow.camera.near = 0.1; // Default is too high sometimes
        spotlight.shadow.camera.far = 1; // Increase if objects disappear from shadow

        spotlight.shadow.bias = -0.001; // Adjust this value if shadow acne appears
        spotlight.shadow.mapSize.width = 1024; // Increase shadow resolution
        spotlight.shadow.mapSize.height = 1024;

        scene.add(ambient, spotlight);
        return () => {
            scene.remove(ambient, spotlight);
        };
    }, [scene]);

    // Configure shadows
    useEffect(() => {
        scene.traverse((node) => {
            if ((node as THREE.Mesh).isMesh) {
                node.castShadow = true;
                node.receiveShadow = true;
                if (node.name.includes('C_glasses_lenses_MESH')) {
                    node.castShadow = false;
                    node.receiveShadow = false;
                }
            }
        });
    }, [scene]);

    // Animation control
    useEffect(() => {
        if (!actions) return;

        // Map modes to animation prefixes
        const animMap: Record<typeof mode, string[]> = {
            TALKING: ['Speaking00', 'Speaking01', 'Speaking02'],
            LISTENING: ['Listening00', 'Listening01', 'Listening02'],
            ENCOURAGING: ['Encouraging00', 'Encouraging01', 'Encouraging02'],
            IDLE: ['Idle00', 'Idle01', 'Idle02'],
        };

        const currentModeAnimList = animMap[mode];

        const pickRandomAnimation = (animList: AnimationAction[], lastAnimPlayed: AnimationAction | null) => {
            const lastAnimPlayedName = lastAnimPlayed?.getClip().name;
            let chosenAnim: AnimationAction;
            do {
                /** Ignoring the below line for sonar checks */
                chosenAnim = animList[Math.floor(Math.random() * animList.length)]; // NOSONAR
            } while (lastAnimPlayed && chosenAnim.getClip().name === lastAnimPlayedName);
            return chosenAnim;
        };

        const play = () => {
            const currentModeActionList = currentModeAnimList.map((m) => actions[m]) as AnimationAction[];

            // choose animation from array
            const lastAnimPlayed = lastAnimPlayedRef.current;
            const chosenAnim = pickRandomAnimation(currentModeActionList, lastAnimPlayed);

            // fade out others
            Object.values(actions).forEach((act) => {
                if (act !== chosenAnim) act?.fadeOut(0.5);
            });

            //play chosen animation
            chosenAnim?.reset().fadeIn(0.5).play();

            lastAnimPlayedRef.current = chosenAnim;
        };

        if (currentModeAnimList) {
            // Initial play and repeat
            play();
            intervalRef.current = setInterval(play, 5000);
        } else {
            console.warn('Unknown animation mode:', mode);
            pushFaroEvent('avatar_unknown_animation_mode', { mode });
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [actions, mode]);

    return (
        <>
            <primitive object={scene} /> 
            <CameraSetup />
            <Stage environment={null} adjustCamera={false} />
        </>
    );
};

const CameraSetup = () => {
    const { camera } = useThree();
    useEffect(() => {
        camera.position.set(0, 0, 1);
        camera.lookAt(0, 0, 0);
    }, [camera]);
    return null;
};

Avatar.displayName = 'Avatar';
